<template>
  <div class="c-select" :class="{ 'is-single': isSingle }">
    <template v-if="isSingle">
      <input
        class="input"
        :value="basicDateFormat(date)"
        type="text"
        :maxlength="isDisplayDayOfWeek ? 13 : 10"
        :pattern="singleDatePattern"
        @focus="isShow = true"
        @blur="isShow = false"
        @keydown.enter.prevent="dateChangeSingle($event.target.value)"
      >
      <div
        :class="`wrapper ${opens} ${isShow ? 'is-show' : ''}`"
        tabindex="-1"
      >
        <div class="calendar">
          <VDatePicker
            v-model="date"
            color="red"
            :masks="masks"
            :min-date="disabledDates.from ? disabledDates.from.toDate() : undefined"
            :max-date="disabledDates.to ? disabledDates.to.toDate() : undefined"
          />
        </div>
      </div>
    </template>

    <template v-else>
      <input
        ref="input"
        class="input"
        :value="`${basicDateFormat(date.start)} - ${basicDateFormat(date.end)}`"
        type="text"
        maxlength="23"
        pattern="\d{4}/\d{1,2}/\d{1,2} - \d{4}/\d{1,2}/\d{1,2}"
        @focus="isShow = true"
        @blur="isShow = false"
        @keydown.enter.prevent="dateChange($event.target.value)"
      >
      <div
        :class="`wrapper ${opens} ${isShow ? 'is-show' : ''}`"
        tabindex="-1"
      >
        <div class="calendar">
          <VDatePicker
            v-model="date"
            color="red"
            :columns="2"
            :step="1"
            :masks="masks"
            :min-date="disabledDates.from ? disabledDates.from.toDate() : undefined"
            :max-date="disabledDates.to ? disabledDates.to.toDate() : undefined"
            is-range
          />
        </div>

        <div v-if="!isInteractive" class="c-button c-button--right">
          <button class="c-button__item" type="button" @click="cancel">
            {{ $t('labels.cancel') }}
          </button>
          <button class="c-button__item c-button__item--primary" type="button" @click="syncDate">
            {{ $t('labels.select') }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
// FIXME: package.json で @popperjs/core をインストールしているが、v-calendar が依存しているため
//        Uncaught Error: Cannot find module '@popperjs/core'
//        もしバージョンアップなどで @popperjs/core を内部でインス取るするようになっていたら消したい
import { DatePicker as VDatePicker } from 'v-calendar'
import 'v-calendar/dist/style.css'
import moment from 'moment'
import { i18n } from '../plugins/i18n'

export default {
  name: 'DatePicker',
  components: {
    VDatePicker,
  },
  props: {
    isSingle: {
      type: Boolean,
      default: false,
    },
    initialDates: {
      type: [Date, Object], // Date or { startDate: Date, endDate: Date }
      default: () => {
        return this.isSingle ? null : {
          startDate: null,
          endDate: null,
        }
      }
    },
    opens: {
      type: String, // center, left, right
      default: 'center'
    },
    disabledDates: {
      type: Object,
      default: () => ({
        from: null,
        to: moment(),
      }),
    },
    isDisplayDayOfWeek: {
      type: Boolean,
      default: false,
    },
    isInteractive: {
      type: Boolean,
      default: false,
    }
  },
  emits: ['input'],
  data() {
    return {
      date: null,
      isShow: false,
      masks: {
        title: 'YYYY MMMM',
        input: this.isDisplayDayOfWeek ? 'YYYY/MM/DD(WWW)' : 'YYYY/MM/DD',
      },
    }
  },
  computed: {
    // 複数の値をwatchするためのハック
    initialDatesAndSingle() {
      return [this.isSingle, this.initialDates]
    },
    singleDatePattern() {
      return this.isDisplayDayOfWeek ? /\d{4}\/\d{1,2}\/\d{1,2}\(.\)/ : /\d{4}\/\d{1,2}\/\d{1,2}/
    }
  },
  watch: {
    initialDatesAndSingle: {
      immediate: true,
      handler(value) {
        const [isSingle, initialDates] = value
        this.date = isSingle ? initialDates : {
          start: initialDates.startDate,
          end: initialDates.endDate,
        }
      }
    },
    date(newValue, oldValue) {
      if(this.isSingle && oldValue.getTime() === newValue.getTime()) return
      if(this.isInteractive && oldValue.start.getTime() === newValue.start.getTime() && oldValue.end.getTime() === newValue.end.getTime()) return

      // 選択をクリックしなくてもイベントを親に伝えるために監視しておく
      if(this.isInteractive || this.isSingle) {
        this.syncDate(newValue)
      }
    }
  },
  created() {
    moment.locale(this.$i18n.locale)
  },
  methods: {
    basicDateFormat(date) {
      const format = this.isDisplayDayOfWeek ? i18n.t('format.date.includeWday') : i18n.t('format.date.default')
      return date ? moment(date).format(format) : ''
    },
    stringToDate(str) {
      const arr = str.split('/')
      return new Date(Number(arr[0]), Number(arr[1]) - 1, Number(arr[2]))
    },
    close() {
      const element = document.activeElement
      element.blur()
    },
    dateChange(value) {
      const dates = value.split(' - ')
      // 2つのカレンダー文字列が合格してなかったら早期リターン
      if (!dates.every(date => /\d{4}\/\d{1,2}\/\d{1,2}/.test(date))) {
        return
      }

      dates.sort()

      this.date = {
        start: this.stringToDate(dates[0]),
        end: this.stringToDate(dates[1]),
      }
    },
    dateChangeSingle(value) {
      if (!this.singleDatePattern.test(value)) {
        return
      }
      const date = this.isDisplayDayOfWeek ? value.slice( 0, -3 ) : value
      this.date = this.stringToDate(date)
    },
    cancel() {
      this.date = {
        start: this.initialDates.startDate instanceof moment ? this.initialDates.startDate.toDate() : this.initialDates.startDate,
        end: this.initialDates.endDate instanceof moment ? this.initialDates.endDate.toDate() : this.initialDates.endDate
      }
      this.close()
    },
    syncDate(date) {
      if (this.isSingle) this.date = date
      const dates = {
        startDate: this.date.start,
        endDate: this.date.end,
      }
      this.$emit('input', this.isSingle ? this.date : dates)
      this.close()
    },
  }
}
</script>

<style lang="scss" scoped>
.c-select {
  position: relative;
  width: 256px;

  &.is-single {
    width: 100%;
  }

  .input {
    font-size: 14px;
  }
}
.wrapper {
  visibility: hidden;
  position: absolute;
  z-index: 2; // c-select の ▼ が z-index: 1; でかぶってしまうため 2 にしている
  background-color: #ffffff;
  box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1),0 4px 6px -2px rgba(0,0,0,0.05);
  border-radius: 4px;
  padding-top: 1rem;
  opacity: 0;
  transition: 0.2s ease-in-out;

  &.center {
    transform: translate(-25%, 0);
  }
  &.left {
    transform: translate(-50%, 0);
  }
  &.right {
    transform: translate(0, 0);
  }

  &.is-show, &:focus-within, &:hover, &:active {
    opacity: 1;
    visibility: visible;
  }

  @media screen and (max-width: 768px) {
    transform: translate(0, -50%) !important;
  }
}
.calendar {
  border: none;
  border-radius: 4px 4px 0 0;
  border-bottom: 1px solid #e4e7eb;
}

.c-button {
  margin: 8px;

  &:not(:first-child) {
    margin-top: 16px;
  }
}

:deep(.vc-container) {
  --red-100: #ffffff;
  --red-200: rgba(233, 68, 106, 0.2);
  --red-300: rgba(233, 68, 106, 0.3);
  --red-400: rgba(233, 68, 106, 0.4);
  --red-500: rgba(233, 68, 106, 0.5);
  --red-600: #E9446A;
  --red-700: #E9446A;
  --red-800: #3A3B3F;
  --red-900: #3A3B3F;
  border: none;

  @media screen and (max-width: 768px) {
    .vc-pane-layout {
      grid-template-columns: none !important;
      flex-direction: row;
    }
  }

  .is-today > .vc-day-content:first-child {
    background-color: #e2e2e2;
  }
}
</style>
